import FormControl from '@mui/material/FormControl';
import { createElement, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import theme from 'src/styles/theme';
import type { StoredPhoneNumberData } from './authentication/store/pageDataTypes';

type PhoneNumberInputProps = {
  startingPhoneValue: StoredPhoneNumberData;
  dropdownDisabled?: boolean;
  inputName?: string;
  phoneInputRef?: React.MutableRefObject<HTMLInputElement | undefined>;
  onChangeHandler?: (value: string, country: CountryData) => void;
  validityHandler?: (validFormat: boolean) => void;
};

export function PhoneNumberInput({
  dropdownDisabled = true,
  startingPhoneValue,
  inputName = 'phone',
  phoneInputRef,
  onChangeHandler,
  validityHandler,
}: PhoneNumberInputProps) {
  const phoneFormControlRef =
    useRef<HTMLDivElement>() as React.MutableRefObject<HTMLDivElement>;

  const [phoneData, setPhoneData] = useState<StoredPhoneNumberData>({
    country: startingPhoneValue.country || 'us',
    countryDialCode: startingPhoneValue.countryDialCode || '1',
    phoneNumber: startingPhoneValue.phoneNumber || '',
  });

  // This useEffect creates a custom-structured custom label for the PhoneInput so that we can implement
  // gradient typography and also cover the border
  useEffect(() => {
    if (!!phoneFormControlRef.current) {
      const phoneInput: HTMLDivElement | null =
        phoneFormControlRef.current.querySelector('.react-tel-input');

      if (!!phoneInput) {
        if (!!phoneInputRef) {
          // Update the given ref, if we were given one
          const phoneInputEl: HTMLInputElement | null =
            phoneInput.querySelector('.phone-number-input');
          if (!!phoneInputEl) phoneInputRef.current = phoneInputEl;
        }

        const customLabel: HTMLDivElement = document.createElement('div');
        customLabel.classList.add('special-label');
        customLabel.style.background = '#011d3c'; // Matches the modal background elevation + blur
        const customLabelInner: HTMLDivElement = document.createElement('div');

        // This basically just creates a <GradientTypography variant="caption">Phone Number</GradientTypography>
        // inside customLabelInner, but using the React DOM
        const root = createRoot(customLabelInner);
        const test = createElement(
          'span',
          {
            style: {
              ...theme.typography.inputLabel,
              background: theme.palette.primary.vertical,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            },
          },
          'Phone Number',
        );
        root.render(test);

        customLabel.appendChild(customLabelInner);
        phoneInput.appendChild(customLabel);
      }
    }
  }, [phoneFormControlRef, phoneInputRef]);

  if (!startingPhoneValue) return null;

  return (
    <FormControl
      ref={phoneFormControlRef}
      sx={{
        '& .input-gradient-border': {
          position: 'relative',
        },
        '& .input-gradient-border:before': {
          content: '""',
          position: 'absolute',
          zIndex: '-1',
          inset: '0',
          padding: '2px',
          borderRadius: '4px',
          background: theme.palette.primary.vertical,
          WebkitMask:
            'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
        },
        '& .phone-number-input': {
          color: theme.palette.text.primary,
        },
        '& .flag-dropdown .arrow': {
          '&.up': {
            borderBottomColor: 'white !important',
          },
          '&:not(.up)': {
            borderTopColor: 'white !important',
          },
        },
        '& .flag-dropdown .selected-flag': {
          width: dropdownDisabled ? 'auto' : 'unset',
        },
        '& .input-gradient-border .special-label': {
          // Adjust positioning on the border
          top: 0,
          left: '12px',
          transform: 'translateY(-50%)',
        },
        '& .react-tel-input .country-list': {
          background: theme.palette.background.elevation[8],
          backdropFilter: theme.glass[8],
          '& li:hover': {
            background: theme.palette.background.elevation[12],
            backdropFilter: theme.glass[12],
          },
        },
        '& .react-tel-input .country-list .country.highlight': {
          background: theme.palette.background.elevation[12],
          backdropFilter: theme.glass[12],
        },
      }}
    >
      <PhoneInput
        disableDropdown={dropdownDisabled}
        country={startingPhoneValue.country}
        value={
          startingPhoneValue.countryDialCode + startingPhoneValue.phoneNumber
        }
        specialLabel=""
        containerClass={'input-gradient-border'}
        inputClass={'phone-number-input'}
        inputStyle={{
          background: 'transparent',
          boxShadow: 'none',
          paddingLeft: dropdownDisabled ? '47px' : undefined,
          ...theme.typography.inputText,
        }}
        inputProps={{
          name: inputName,
          required: true,
          autoFocus: true,
        }}
        onChange={(value, country: CountryData) => {
          const phoneNumber = value.slice(country.dialCode.length);
          setPhoneData({
            ...phoneData,
            country: country.countryCode,
            countryDialCode: country.dialCode,
            phoneNumber: phoneNumber,
          });
          !!onChangeHandler && onChangeHandler(value, country);
        }}
        isValid={(value: string, country: object, countries: object[]) => {
          const countryData = country as CountryData,
            countriesData = countries as CountryData[];

          const isValidNumber =
            /^[0-9]+$/g.test(value) &&
            countriesData.some((givenCountry: CountryData) => {
              // Is there some country with this dial code, format length, and name?
              return (
                value.startsWith(givenCountry.dialCode) &&
                givenCountry.name === countryData.name &&
                (countryData.format.match(/\./g) || []).length === value.length
              );
            });

          !!validityHandler && validityHandler(isValidNumber);

          return isValidNumber;
        }}
      />
    </FormControl>
  );
}
